import { Doc } from '@dadohr/shared/domainTypes/document';
import { EMPTY_ARR } from '@dadohr/shared/domainTypes/emptyConstants';
import { ChartHopFieldCompact } from '@dadohr/shared/domainTypes/integrations/charthop';
import {
  getSupportedMergeSourcePropertyMappings,
  MERGE_FIELDS_MAP,
  MergeEmployeePropertyMapTaget
} from '@dadohr/shared/domainTypes/integrations/merge';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import {
  CompositeUserFieldKey,
  DefaultDadoField,
  IUser,
  UserImportDataSource,
  UserImportMapping,
  UserImportSourceType
} from '@dadohr/shared/domainTypes/user';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { Button, IconButton, TextField, Typography } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { capitalize, isEqual, keyBy, sortBy } from 'lodash';
import {
  ArrowRight,
  ChevronDown,
  ChevronUp,
  HelpCircle,
  Trash2,
  X
} from 'lucide-react';
import React, { SetStateAction, useMemo, useRef, useState } from 'react';
import { AlertBox } from '../../../../../components/AlertBox';
import { AutocompleteSingleSimpleKeyed } from '../../../../../components/Autocomplete';
import { ButtonWithPromise } from '../../../../../components/ButtonWithPromise';
import { FormRow } from '../../../../../components/Form/FormRow';
import { InfoBox } from '../../../../../components/InfoBox';
import { Loader } from '../../../../../components/Loader';
import { MailTo } from '../../../../../components/MailTo';
import { useDialogState } from '../../../../../hooks/useDialogState';
import { usePromise } from '../../../../../hooks/usePromise';
import { useSnackbar } from '../../../../../hooks/useSnackbar';
import { UnsafeTFunction, useUnsafeTranslation } from '../../../../../i18next';
import {
  FlexContainer,
  FlexContainerVertical
} from '../../../../../layout/Flex';
import { joinWithCommaAndAnd } from '../../../../../services/enumerate';
import { updateDoc } from '../../../../../services/firebase';
import { LightMdToJsx } from '../../../../../services/formatter';
import {
  compositeUserFields,
  standardUserFields
} from '../../../../../services/user';
import { AddButton } from '../../../../Experiences/components/RuleBuilder';
import { getMappingsWithoutEmpty } from './sources/common';
import { ImportSourceIntegrationInfo } from './sources/meta';

const MultiMappingHints = ({
  mappings,
  getLabel
}: {
  mappings: UserImportMapping[];
  getLabel: (key: string) => string;
}) => {
  const { t } = useUnsafeTranslation();
  const fdawMappings = mappings.filter(
    (m) => m.dadoKey === DefaultDadoField.HIRE_DATE
  );
  const ldawMappings = mappings.filter(
    (m) => m.dadoKey === DefaultDadoField.TERMINATION_DATE
  );
  if (!(fdawMappings.length > 1 || ldawMappings.length > 1)) {
    return null;
  }
  // console.log('mappings', fdawMappings, ldawMappings);
  return (
    <FlexContainerVertical spacing={1} fullWidth marginTop={2}>
      {fdawMappings.length > 1 && (
        <InfoBox fullWidth color="yellow">
          <LightMdToJsx>
            {t(
              `You have mapped {{ fields }} to Dado's First Day at Work field. Dado will use the latest date from these fields as the employee's First Day at Work.`,
              {
                fields: joinWithCommaAndAnd(
                  fdawMappings.map(
                    (m) => `**${getLabel(m.otherKey) || m.otherKey}**`
                  ),
                  t
                )
              }
            )}
          </LightMdToJsx>
        </InfoBox>
      )}
      {ldawMappings.length > 1 && (
        <InfoBox fullWidth color="yellow">
          <LightMdToJsx>
            {t(
              `You have mapped {{ fields }} to Dado's Last Day at Work field. Dado will use the latest date from these fields as the employee's Last Day at Work.`,
              {
                fields: joinWithCommaAndAnd(
                  ldawMappings.map(
                    (m) => `**${getLabel(m.otherKey) || m.otherKey}**`
                  ),
                  t
                )
              }
            )}
          </LightMdToJsx>
        </InfoBox>
      )}
    </FlexContainerVertical>
  );
};

export type DadoFieldOption = {
  label: string;
  key: string;
};

type MultiMapping = {
  main: UserImportMapping[];
  others?: UserImportMapping[][];
};

const isMappingComplete = (mapping: UserImportMapping): boolean =>
  !!(mapping.dadoKey && mapping.otherKey);

const isMappingWithEmail = (mapping: UserImportMapping): boolean =>
  ['email', 'personalEmail'].includes(mapping.dadoKey);

const isMappingsValid = (mappings: UserImportMapping[]): boolean =>
  mappings.every((m) => isMappingComplete(m)) &&
  mappings.some((m) => isMappingWithEmail(m));

const isMultiMappingValid = (mapping: MultiMapping): boolean => {
  const isMainValid = isMappingsValid(mapping.main);

  if (mapping.others) {
    return isMainValid && mapping.others?.every((m) => isMappingsValid(m));
  }

  return isMainValid;
};

const toCustomFieldsKey = (k: string) => `customFields.${k}`;

const createEmptyUserImportMapping = (): UserImportMapping => ({
  dadoKey: '',
  otherKey: ''
});

export const customFieldToUserImportMapping = (
  def: CustomUserField,
  opts: { defaultToEmpty?: boolean } = {}
): UserImportMapping => {
  const dadoKey = toCustomFieldsKey(def.key);
  return {
    dadoKey,
    otherKey: opts.defaultToEmpty ? '' : def.label
  };
};

export const COMPOSITE_MAPPINGS: {
  $FULL_NAME$: UserImportMapping;
  $FULL_NAME_LN_FN$: UserImportMapping;
} = {
  $FULL_NAME$: {
    dadoKey: '$FULL_NAME$',
    otherKey: 'Full Name'
  },
  $FULL_NAME_LN_FN$: {
    dadoKey: '$FULL_NAME$',
    otherKey: 'Full Name (Last Name, First Name)'
  }
};

export const getDefaultMappings = (
  customUserFields: CustomUserField[],
  t: UnsafeTFunction
): UserImportMapping[] => {
  return [
    ...standardUserFields(t).map((def) => ({
      dadoKey: def.key,
      otherKey: def.label
    })),
    ...customUserFields
      .filter((c) => !c.restricted && c.type !== 'smartDate')
      .map((c) => customFieldToUserImportMapping(c))
  ];
};

const Grid2 = styled('div')((p) => ({
  display: 'grid',
  gridTemplateColumns:
    'minmax(min-content, 400px) min-content minmax(min-content, 400px) min-content',
  gridColumnGap: p.theme.spacing(1),
  gridRowGap: p.theme.spacing(1),
  alignItems: 'center'
}));

const Grid2Row = styled('div')`
  grid-column: span 4;
  margin-block-start: ${(p) => p.theme.spacing(-1)}px;
`;

export const CustomImportMappingFormToggle = ({
  value,
  onChange
}: {
  value: boolean;
  onChange: React.Dispatch<SetStateAction<boolean>>;
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <FormRow>
      <FlexContainer
        fullWidth
        role="button"
        justifyContent="space-between"
        onClick={() => onChange((x) => !x)}
        style={{ cursor: 'pointer' }}
      >
        <Typography variant="h5">{t('Field Mappings')}</Typography>
        <IconButton>
          {value ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </IconButton>
      </FlexContainer>
    </FormRow>
  );
};

export const CustomImportMultiMappingForm2 = ({
  additionalCompositeFields,
  enableSave = false,
  fieldTerm,
  integrationName,
  userFields,
  value,
  onChange,
  onDelete,
  onSave
}: {
  additionalCompositeFields?: CompositeUserFieldKey[];
  enableSave?: boolean;
  fieldTerm?: string;
  integrationName: string;
  userFields: CustomUserField[];
  value: MultiMapping;
  onChange: (nextValue: MultiMapping) => void;
  onDelete?: () => Promise<any>;
  onSave?: () => Promise<any>;
}) => {
  const { t } = useUnsafeTranslation();
  const [isMappingValid, setIsMappingValid] = useState(true);

  const onMappingChange = (mapping: MultiMapping) => {
    setIsMappingValid(isMultiMappingValid(mapping));
    onChange(mapping);
  };

  return (
    <>
      <CustomImportMappingForm2
        additionalCompositeFields={additionalCompositeFields}
        userFields={userFields}
        integrationName={integrationName}
        name={t('Default Field Mapping')}
        fieldTerm={fieldTerm}
        mappings={value.main}
        onChange={(mapping) =>
          onMappingChange({
            main: mapping,
            others: value.others
          })
        }
      />

      {value.others?.map((other, index) => (
        <CustomImportMappingForm2
          additionalCompositeFields={additionalCompositeFields}
          userFields={userFields}
          integrationName={integrationName}
          key={index}
          name={t('Field Mapping Set {{number}}', { number: 2 + index })}
          mappings={other}
          onChange={(mapping) =>
            onMappingChange({
              main: value.main,
              others: value.others?.map((v, i) => (i === index ? mapping : v))
            })
          }
          onDelete={() =>
            onMappingChange({
              main: value.main,
              others: value.others?.filter((v) => v !== other)
            })
          }
        />
      ))}

      <FlexContainer justifyContent={'space-between'}>
        <AddButton
          label={t('Add Field Mapping set')}
          onClick={() =>
            onChange({
              ...value,
              others: value.others
                ? [...value.others, [createEmptyUserImportMapping()]]
                : [[createEmptyUserImportMapping()]]
            })
          }
        />

        {onSave && (
          <ButtonWithPromise
            color="primary"
            variant="contained"
            disabled={!isMappingValid || !enableSave}
            pending={`${t('Saving')}...`}
            onClick={onSave}
          >
            {t('Save changes')}
          </ButtonWithPromise>
        )}
      </FlexContainer>
    </>
  );
};

const CustomImportMappingFormRow = ({
  userFields,
  value,

  onBlur,
  onChange,
  onRemove
}: {
  userFields: DadoFieldOption[];
  value: UserImportMapping;
  onBlur: () => any;
  onChange: (mapping: UserImportMapping) => any;
  onRemove: () => any;
}) => {
  const { closeDialog } = useDialogState();
  const { t } = useUnsafeTranslation();
  const theme = useTheme();

  const [isDadoDirty, setIsDadoDirty] = useState(false);
  const [isOtherDirty, setIsOtherDirty] = useState(false);

  const { enqueueSnackbarError } = useSnackbar();

  const removeHandler = () => {
    closeDialog();
    onRemove();
  };

  return (
    <>
      <div>
        <TextField
          value={value.otherKey}
          fullWidth
          onBlur={() => {
            setIsOtherDirty(true);

            if (isDadoDirty && isOtherDirty) {
              onBlur();
            }
          }}
          onChange={(e) => {
            setIsOtherDirty(true);
            onChange({ ...value, otherKey: e.target.value });
          }}
          aria-labelledby="heading-field-source"
        />
      </div>
      <div>
        <ArrowRight size="14" color={theme.custom.colors.gray.w500} />
      </div>
      <div>
        <AutocompleteSingleSimpleKeyed
          value={value.dadoKey}
          options={sortBy(userFields, (x) => x.label)}
          toSelectKey={(o) => o.key}
          toLabel={(o) => o.label}
          fullWidth
          onBlur={() => {
            setIsDadoDirty(true);

            if (isDadoDirty && isOtherDirty) {
              onBlur();
            }
          }}
          onChange={(nextDadoKey) => {
            if (DISALLOWED_DADO_MAPPINGS_KEYS.includes(nextDadoKey as any)) {
              const label =
                userFields.find((x) => x.key === nextDadoKey)?.label ||
                nextDadoKey;
              enqueueSnackbarError(
                t(
                  `It's not currently possible to import Address data into Dado`,
                  {
                    label
                  }
                )
              );
              return;
            }
            setIsDadoDirty(true);
            onChange({ ...value, dadoKey: nextDadoKey });
          }}
        />
      </div>
      <div>
        <IconButton aria-label={t('Delete mapping')} onClick={removeHandler}>
          <X size="14" />
        </IconButton>
      </div>

      {isDadoDirty && isOtherDirty && !isMappingComplete(value) && (
        <Grid2Row>
          <AlertBox color="red">
            {t('Please supply both the source and Dado field names.')}
          </AlertBox>
        </Grid2Row>
      )}
    </>
  );
};

const FieldMappingContainer = styled('div')`
  background-color: ${(p) => p.theme.palette.grey[100]};
  border-radius: ${(p) => p.theme.custom.border.radius()}px;
  padding: ${(p) => p.theme.spacing(2)}px;
  margin-block-end: ${(p) => p.theme.spacing(2)}px;
  width: 100%;
`;

export const HIDDEN_USER_FIELD_KEYS: (keyof IUser)[] = ['active'];
export const DISALLOWED_DADO_MAPPINGS_KEYS: (keyof IUser)[] = ['address'];

export const MergeImportMappingForm = ({
  additionalCompositeFields = [],
  fieldTerm,
  integrationName,
  mappings,
  name,
  userFields,
  displayNames,
  onChange,
  onDelete,
  supportedMergeFields
}: {
  additionalCompositeFields?: CompositeUserFieldKey[];
  fieldTerm?: string;
  integrationName: string;
  mappings: UserImportMapping[];
  name: string;
  userFields: CustomUserField[];
  displayNames?: Record<string, string>;
  onChange: (mapping: UserImportMapping[]) => any;
  onDelete?: () => any;
  supportedMergeFields: MergeEmployeePropertyMapTaget[];
}) => {
  const { t } = useUnsafeTranslation();
  const theme = useTheme();
  const fieldTermOrDefault = fieldTerm ?? t('field');

  const [isDirty, setIsDirty] = useState(false);
  const version = useRef(0);

  const userFieldsWithComposites: DadoFieldOption[] = useMemo(
    () =>
      [
        ...compositeUserFields(additionalCompositeFields, t),
        ...userFields
      ].filter((f) => !HIDDEN_USER_FIELD_KEYS.includes(f.key as any)),
    [userFields, additionalCompositeFields, t]
  );

  const isEmailSet = useMemo(
    () => mappings.some((m) => isMappingWithEmail(m)),
    [mappings]
  );

  return (
    <FieldMappingContainer>
      <FlexContainer justifyContent="space-between">
        <Typography
          component="h3"
          variant="h6"
          style={{ marginBlockEnd: theme.spacing(2) }}
        >
          {name}
        </Typography>

        {onDelete && (
          <Button
            color="primary"
            startIcon={<Trash2 size={16} />}
            onClick={onDelete}
          >
            {t('Remove Field Mapping set')}
          </Button>
        )}
      </FlexContainer>

      <Typography style={{ marginBlockEnd: theme.spacing(2) }}>
        {t(
          'Select which {{ fieldTerm }} in {{ integration }} will sync to which Data Field in Dado',
          {
            fieldTerm: fieldTermOrDefault,
            integration: integrationName
          }
        )}
      </Typography>

      {isDirty && !isEmailSet && (
        <AlertBox color="red">
          {t('Please add at least one email field.')}
        </AlertBox>
      )}

      <Grid2>
        <div>
          <Typography variant="caption" id="heading-field-source">
            {t('import.mapping.integration', {
              fieldTerm: capitalize(fieldTermOrDefault),
              integration: integrationName
            })}
          </Typography>
        </div>

        <div>&nbsp;</div>

        <div>
          <Typography variant="caption" id="heading-field-dado">
            {t('import.mapping.dado')}
          </Typography>
        </div>

        <div>&nbsp;</div>

        {mappings.map((mapping, i) => (
          <MergeImportMappingFormRow
            key={`${version.current}-${i}`}
            userFields={userFieldsWithComposites}
            value={mapping}
            onBlur={() => setIsDirty(true)}
            onChange={(mappingNew) => {
              onChange(mappings.map((mo, j) => (i === j ? mappingNew : mo)));
            }}
            onRemove={() => {
              if (mappings.length > 1) {
                onChange(mappings.filter((mo, j) => i !== j));
              } else {
                // Increment version generates new keys for this component, resetting their internal state
                version.current++;
                onChange([createEmptyUserImportMapping()]);
              }
            }}
            supportedMergeFields={supportedMergeFields}
          />
        ))}

        <div>
          <AddButton
            label={t('Add field')}
            onClick={() => {
              setIsDirty(true);
              onChange([...mappings, createEmptyUserImportMapping()]);
            }}
          />
        </div>
      </Grid2>
      <MultiMappingHints
        mappings={mappings}
        getLabel={(f) =>
          (MERGE_FIELDS_MAP as any)[f]?.displayName || ''
        }
      />
    </FieldMappingContainer>
  );
};

const NoResults = ({ textSearch }: { textSearch: string }) => {
  const { t } = useUnsafeTranslation();

  return (
    <FlexContainer direction="column" alignItems={'start'}>
      <Typography variant="body1">
        <LightMdToJsx>
          {t('Field **{{ textSearch }}** not found.', { textSearch })}
        </LightMdToJsx>
      </Typography>
      <InfoBox color="yellow">
        <FlexContainer alignItems="flex-start">
          <HelpCircle size={22} />
          <Typography variant="body1">
            {t('Missing a field from import source? Reach out to ')}
            <MailTo email="help@dadohr.com" color="primary">
              help@dadohr.com
            </MailTo>
          </Typography>
        </FlexContainer>
      </InfoBox>
    </FlexContainer>
  );
};

const MergeImportMappingFormRow = ({
  userFields,
  value,
  supportedMergeFields,

  onBlur,
  onChange,
  onRemove
}: {
  userFields: DadoFieldOption[];
  value: UserImportMapping;
  supportedMergeFields: MergeEmployeePropertyMapTaget[];
  onBlur: () => any;
  onChange: (mapping: UserImportMapping) => any;
  onRemove: () => any;
}) => {
  const { closeDialog } = useDialogState();
  const { t } = useUnsafeTranslation();
  const theme = useTheme();

  const [isDadoDirty, setIsDadoDirty] = useState(false);
  const [isOtherDirty, setIsOtherDirty] = useState(false);

  const [searchText, setSearchText] = useState('');

  const { enqueueSnackbarError } = useSnackbar();

  const removeHandler = () => {
    closeDialog();
    onRemove();
  };

  return (
    <>
      <div>
        <Autocomplete
          options={supportedMergeFields}
          value={value.otherKey}
          onChange={(e, v) => {
            if (v) {
              setIsOtherDirty(true);
              onChange({ ...value, otherKey: v });
            }
          }}
          onInputChange={(event, newInputValue) => {
            setSearchText(newInputValue);
          }}
          noOptionsText={<NoResults textSearch={searchText} />}
          getOptionLabel={(option) => {
            const mergeSourceField = option as MergeEmployeePropertyMapTaget;
            if (!mergeSourceField) {
              // e.g. happens when adding a new blank field, waiting for additional
              // user input
              return '';
            }
            // TODO fix typing
            let displayName = (MERGE_FIELDS_MAP as any)[mergeSourceField]
              ?.displayName;
            if (!displayName) {
              console.log('unknown property ' + mergeSourceField);
              console.log(JSON.stringify(supportedMergeFields));
              displayName = 'unknown';
            }
            return displayName;
          }}
          renderInput={(params) => <TextField {...params} />}
        />
      </div>
      <div>
        <ArrowRight size="14" color={theme.custom.colors.gray.w500} />
      </div>
      <div>
        <AutocompleteSingleSimpleKeyed
          value={value.dadoKey}
          options={sortBy(userFields, (x) => x.label)}
          toSelectKey={(o) => o.key}
          toLabel={(o) => o.label}
          fullWidth
          onBlur={() => {
            setIsDadoDirty(true);

            if (isDadoDirty && isOtherDirty) {
              onBlur();
            }
          }}
          onChange={(nextDadoKey) => {
            if (DISALLOWED_DADO_MAPPINGS_KEYS.includes(nextDadoKey as any)) {
              const label =
                userFields.find((x) => x.key === nextDadoKey)?.label ||
                nextDadoKey;
              enqueueSnackbarError(
                t(
                  `It's not currently possible to import Address data into Dado`,
                  {
                    label
                  }
                )
              );
              return;
            }
            setIsDadoDirty(true);
            onChange({ ...value, dadoKey: nextDadoKey });
          }}
        />
      </div>
      <div>
        <IconButton aria-label={t('Delete mapping')} onClick={removeHandler}>
          <X size="14" />
        </IconButton>
      </div>

      {isDadoDirty && isOtherDirty && !isMappingComplete(value) && (
        <Grid2Row>
          <AlertBox color="red">
            {t('Please supply both the source and Dado field names.')}
          </AlertBox>
        </Grid2Row>
      )}
    </>
  );
};

type FieldDescriptor = ChartHopFieldCompact;

const AutocompleteImportMappingFormRow = ({
  userFields,
  value,
  fields,

  onBlur,
  onChange,
  onRemove
}: {
  userFields: DadoFieldOption[];
  value: UserImportMapping;
  fields: FieldDescriptor[];
  onBlur: () => any;
  onChange: (mapping: UserImportMapping) => any;
  onRemove: () => any;
}) => {
  const { closeDialog } = useDialogState();
  const { t } = useUnsafeTranslation();
  const theme = useTheme();

  const [isDadoDirty, setIsDadoDirty] = useState(false);
  const [isOtherDirty, setIsOtherDirty] = useState(false);

  const [searchText, setSearchText] = useState('');

  const { enqueueSnackbarError } = useSnackbar();

  const nameKey: keyof FieldDescriptor = 'name';
  const fieldsMap = keyBy(fields, nameKey);

  const removeHandler = () => {
    closeDialog();
    onRemove();
  };

  return (
    <>
      <div>
        <Autocomplete
          options={fields.map((f) => f.name)}
          value={value.otherKey}
          onChange={(e, v) => {
            if (v) {
              setIsOtherDirty(true);
              onChange({ ...value, otherKey: v });
            }
          }}
          onInputChange={(event, newInputValue) => {
            setSearchText(newInputValue);
          }}
          noOptionsText={<NoResults textSearch={searchText} />}
          getOptionLabel={(option) => {
            const fieldDescriptor = fieldsMap[option];
            if (!fieldDescriptor) {
              // e.g. happens when adding a new blank field, waiting for additional
              // user input
              return '';
            }
            let displayName = fieldDescriptor.label || fieldDescriptor.name;
            if (!displayName) {
              displayName = 'unknown';
            }
            return displayName;
          }}
          renderInput={(params) => <TextField {...params} />}
        />
      </div>
      <div>
        <ArrowRight size="14" color={theme.custom.colors.gray.w500} />
      </div>
      <div>
        <AutocompleteSingleSimpleKeyed
          value={value.dadoKey}
          options={sortBy(userFields, (x) => x.label)}
          toSelectKey={(o) => o.key}
          toLabel={(o) => o.label}
          fullWidth
          onBlur={() => {
            setIsDadoDirty(true);

            if (isDadoDirty && isOtherDirty) {
              onBlur();
            }
          }}
          onChange={(nextDadoKey) => {
            if (DISALLOWED_DADO_MAPPINGS_KEYS.includes(nextDadoKey as any)) {
              const label =
                userFields.find((x) => x.key === nextDadoKey)?.label ||
                nextDadoKey;
              enqueueSnackbarError(
                t(
                  `It's not currently possible to import Address data into Dado`,
                  {
                    label
                  }
                )
              );
              return;
            }
            setIsDadoDirty(true);
            onChange({ ...value, dadoKey: nextDadoKey });
          }}
        />
      </div>
      <div>
        <IconButton aria-label={t('Delete mapping')} onClick={removeHandler}>
          <X size="14" />
        </IconButton>
      </div>

      {isDadoDirty && isOtherDirty && !isMappingComplete(value) && (
        <Grid2Row>
          <AlertBox color="red">
            {t('Please supply both the source and Dado field names.')}
          </AlertBox>
        </Grid2Row>
      )}
    </>
  );
};

export const CustomImportMappingForm2 = ({
  additionalCompositeFields = [],
  fieldTerm,
  integrationName,
  mappings,
  name,
  userFields,
  displayNames,
  onChange,
  onDelete
}: {
  additionalCompositeFields?: CompositeUserFieldKey[];
  fieldTerm?: string;
  integrationName: string;
  mappings: UserImportMapping[];
  name: string;
  userFields: CustomUserField[];
  displayNames?: Record<string, string>;
  onChange: (mapping: UserImportMapping[]) => any;
  onDelete?: () => any;
}) => {
  const { t } = useUnsafeTranslation();
  const theme = useTheme();
  const fieldTermOrDefault = fieldTerm ?? t('field');

  const [isDirty, setIsDirty] = useState(false);
  const version = useRef(0);

  const userFieldsWithComposites: DadoFieldOption[] = useMemo(
    () =>
      [
        ...compositeUserFields(additionalCompositeFields, t),
        ...userFields
      ].filter((f) => !HIDDEN_USER_FIELD_KEYS.includes(f.key as any)),
    [userFields, additionalCompositeFields, t]
  );

  const isEmailSet = useMemo(
    () => mappings.some((m) => isMappingWithEmail(m)),
    [mappings]
  );

  return (
    <FieldMappingContainer>
      <FlexContainer justifyContent="space-between">
        <Typography
          component="h3"
          variant="h6"
          style={{ marginBlockEnd: theme.spacing(2) }}
        >
          {name}
        </Typography>

        {onDelete && (
          <Button
            color="primary"
            startIcon={<Trash2 size={16} />}
            onClick={onDelete}
          >
            {t('Remove Field Mapping set')}
          </Button>
        )}
      </FlexContainer>

      <Typography style={{ marginBlockEnd: theme.spacing(2) }}>
        {t(
          'Select which {{ fieldTerm }} in {{ integration }} will sync to which Data Field in Dado',
          {
            fieldTerm: fieldTermOrDefault,
            integration: integrationName
          }
        )}
      </Typography>

      {isDirty && !isEmailSet && (
        <AlertBox color="red">
          {t('Please add at least one email field.')}
        </AlertBox>
      )}

      <Grid2>
        <div>
          <Typography variant="caption" id="heading-field-source">
            {t('import.mapping.integration', {
              fieldTerm: capitalize(fieldTermOrDefault),
              integration: integrationName
            })}
          </Typography>
        </div>

        <div>&nbsp;</div>

        <div>
          <Typography variant="caption" id="heading-field-dado">
            {t('import.mapping.dado')}
          </Typography>
        </div>

        <div>&nbsp;</div>

        {mappings.map((mapping, i) => (
          <>
            <CustomImportMappingFormRow
              key={`${version.current}-${i}`}
              userFields={userFieldsWithComposites}
              value={mapping}
              onBlur={() => setIsDirty(true)}
              onChange={(mappingNew) => {
                onChange(mappings.map((mo, j) => (i === j ? mappingNew : mo)));
              }}
              onRemove={() => {
                if (mappings.length > 1) {
                  onChange(mappings.filter((mo, j) => i !== j));
                } else {
                  // Increment version generates new keys for this component, resetting their internal state
                  version.current++;
                  onChange([createEmptyUserImportMapping()]);
                }
              }}
            />
          </>
        ))}

        <div>
          <AddButton
            label={t('Add field')}
            onClick={() => {
              setIsDirty(true);
              onChange([...mappings, createEmptyUserImportMapping()]);
            }}
          />
        </div>
      </Grid2>
      <MultiMappingHints
        mappings={mappings}
        getLabel={(f) =>
          userFieldsWithComposites.find((x) => x.key === f)?.label || ''
        }
      />
    </FieldMappingContainer>
  );
};

export const CustomImportMappingFormWithAutocomplete = ({
  additionalCompositeFields = [],
  fieldTerm,
  integrationName,
  mappings,
  name,
  userFields,
  displayNames,
  onChange,
  onDelete,
  fields
}: {
  additionalCompositeFields?: CompositeUserFieldKey[];
  fieldTerm?: string;
  integrationName: string;
  mappings: UserImportMapping[];
  name: string;
  userFields: CustomUserField[];
  displayNames?: Record<string, string>;
  onChange: (mapping: UserImportMapping[]) => any;
  onDelete?: () => any;
  fields: FieldDescriptor[];
}) => {
  const { t } = useUnsafeTranslation();
  const theme = useTheme();
  const fieldTermOrDefault = fieldTerm ?? t('field');

  const [isDirty, setIsDirty] = useState(false);
  const version = useRef(0);

  const userFieldsWithComposites: DadoFieldOption[] = useMemo(
    () =>
      [
        ...compositeUserFields(additionalCompositeFields, t),
        ...userFields
      ].filter((f) => !HIDDEN_USER_FIELD_KEYS.includes(f.key as any)),
    [userFields, additionalCompositeFields, t]
  );

  const isEmailSet = useMemo(
    () => mappings.some((m) => isMappingWithEmail(m)),
    [mappings]
  );

  return (
    <FieldMappingContainer>
      <FlexContainer justifyContent="space-between">
        <Typography
          component="h3"
          variant="h6"
          style={{ marginBlockEnd: theme.spacing(2) }}
        >
          {name}
        </Typography>

        {onDelete && (
          <Button
            color="primary"
            startIcon={<Trash2 size={16} />}
            onClick={onDelete}
          >
            {t('Remove Field Mapping set')}
          </Button>
        )}
      </FlexContainer>

      <Typography style={{ marginBlockEnd: theme.spacing(2) }}>
        {t(
          'Select which {{ fieldTerm }} in {{ integration }} will sync to which Data Field in Dado',
          {
            fieldTerm: fieldTermOrDefault,
            integration: integrationName
          }
        )}
      </Typography>

      {isDirty && !isEmailSet && (
        <AlertBox color="red">
          {t('Please add at least one email field.')}
        </AlertBox>
      )}

      <Grid2>
        <div>
          <Typography variant="caption" id="heading-field-source">
            {t('import.mapping.integration', {
              fieldTerm: capitalize(fieldTermOrDefault),
              integration: integrationName
            })}
          </Typography>
        </div>

        <div>&nbsp;</div>

        <div>
          <Typography variant="caption" id="heading-field-dado">
            {t('import.mapping.dado')}
          </Typography>
        </div>

        <div>&nbsp;</div>

        {mappings.map((mapping, i) => (
          <>
            <AutocompleteImportMappingFormRow
              key={`${version.current}-${i}`}
              userFields={userFieldsWithComposites}
              value={mapping}
              onBlur={() => setIsDirty(true)}
              onChange={(mappingNew) => {
                onChange(
                  mappings.map((mo, j) => {
                    return i === j ? mappingNew : mo;
                  })
                );
              }}
              onRemove={() => {
                if (mappings.length > 1) {
                  onChange(mappings.filter((mo, j) => i !== j));
                } else {
                  // Increment version generates new keys for this component, resetting their internal state
                  version.current++;
                  onChange([createEmptyUserImportMapping()]);
                }
              }}
              fields={fields}
            />
          </>
        ))}

        <div>
          <AddButton
            label={t('Add field')}
            onClick={() => {
              setIsDirty(true);
              onChange([...mappings, createEmptyUserImportMapping()]);
            }}
          />
        </div>
      </Grid2>

      <MultiMappingHints
        mappings={mappings}
        getLabel={(f) =>
          userFieldsWithComposites.find((x) => x.key === f)?.label || ''
        }
      />
    </FieldMappingContainer>
  );
};

export const MergeImportMappingFormContainer = ({
  importDataSourceDocument,
  integrationInfo,
  userFields
}: {
  importDataSourceDocument: Doc<UserImportDataSource>;
  integrationInfo: ImportSourceIntegrationInfo;
  userFields: CustomUserField[];
}) => {
  const [importDataSource, setState] = useState(importDataSourceDocument.data);
  const { enqueueSnackbarSuccess } = useSnackbar();
  const { t } = useUnsafeTranslation();

  const isDataChanged = () =>
    !isEqual(
      importDataSourceDocument.data.customMappings,
      importDataSource.customMappings
    );

  const [isMappingsValidState, setIsMappingsValidState] = useState(true);

  const supportedMergeFields = useMemo(() => {
    if (integrationInfo.mergeHRIS) {
      return getSupportedMergeSourcePropertyMappings(integrationInfo.mergeHRIS);
    }
    return EMPTY_ARR;
  }, [integrationInfo]);

  const onChange = (mappings: UserImportMapping[]) => {
    setState((prevState) => ({
      ...prevState,
      customMappings: mappings
    }));
    setIsMappingsValidState(isMappingsValid(mappings));
  };

  const onSave = async () => {
    await updateDoc(importDataSourceDocument, () =>
      getMappingsWithoutEmpty({
        customMappings: importDataSource.customMappings,
        inferredOtherUsers: importDataSource.inferredOtherUsers || []
      })
    );

    enqueueSnackbarSuccess(t('Mapping saved!'));
  };

  return (
    <>
      <MergeImportMappingForm
        additionalCompositeFields={['$FULL_NAME$']}
        userFields={userFields}
        integrationName={integrationInfo.name}
        name={t('Default Field Mapping')}
        mappings={importDataSource.customMappings}
        supportedMergeFields={supportedMergeFields}
        onChange={onChange}
      />

      <FlexContainer justifyContent="flex-end">
        {onSave && (
          <ButtonWithPromise
            color="primary"
            variant="contained"
            disabled={!isMappingsValidState || !isDataChanged()}
            pending={`${t('Saving')}...`}
            onClick={onSave}
          >
            {t('Save changes')}
          </ButtonWithPromise>
        )}
      </FlexContainer>
    </>
  );
};

export const AutoCompleteMappingFormContainer = ({
  importDataSourceDocument,
  integrationInfo,
  userFields,
  getFields
}: {
  importDataSourceDocument: Doc<UserImportDataSource>;
  integrationInfo: ImportSourceIntegrationInfo;
  userFields: CustomUserField[];
  getFields: () => Promise<{ name: string; label: string }[]>;
}) => {
  const [importDataSource, setState] = useState(importDataSourceDocument.data);
  const { enqueueSnackbarSuccess } = useSnackbar();
  const { t } = useUnsafeTranslation();

  const isDataChanged = () =>
    !isEqual(
      importDataSourceDocument.data.customMappings,
      importDataSource.customMappings
    );

  const [isMappingsValidState, setIsMappingsValidState] = useState(true);

  const [fields, loadingFields] = usePromise(async () => {
    const result = await getFields();
    return result;
  });

  const onChange = (mappings: UserImportMapping[]) => {
    setState((prevState) => ({
      ...prevState,
      customMappings: mappings
    }));
    setIsMappingsValidState(isMappingsValid(mappings));
  };

  const onSave = async () => {
    await updateDoc(importDataSourceDocument, () =>
      getMappingsWithoutEmpty({
        customMappings: importDataSource.customMappings,
        inferredOtherUsers: importDataSource.inferredOtherUsers || []
      })
    );

    enqueueSnackbarSuccess(t('Mapping saved!'));
  };

  if (loadingFields) {
    return <Loader height={250} />;
  }

  return (
    <>
      {fields && (
        <CustomImportMappingFormWithAutocomplete
          additionalCompositeFields={
            importDataSource.type === UserImportSourceType.WORKDAY_REPORT_API
              ? ['$FULL_NAME$', '$FULL_NAME_LN_FN$']
              : ['$FULL_NAME$']
          }
          userFields={userFields}
          integrationName={integrationInfo.name}
          name={t('Default Field Mapping')}
          mappings={importDataSource.customMappings}
          fields={fields}
          onChange={onChange}
        />
      )}

      <FlexContainer justifyContent="flex-end">
        {onSave && (
          <ButtonWithPromise
            color="primary"
            variant="contained"
            disabled={!isMappingsValidState || !isDataChanged()}
            pending={`${t('Saving')}...`}
            onClick={onSave}
          >
            {t('Save changes')}
          </ButtonWithPromise>
        )}
      </FlexContainer>
    </>
  );
};
